import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, CalendarPlus, FileText, Loader2, Pause, Pencil, Play, Plus, ShieldAlert, UserPlus,
} from "lucide-react";
import { Sparkles } from "lucide-react";
import PolicyLink from "@/components/PolicyLink";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { API_URL, formatApiError } from "@/lib/api";
import LevelMeaning from "@/components/LevelMeaning";
import {
  ATTENDANCE_LABELS, EXTENSION_REASON_LABELS, FINAL_REVIEW_DECISIONS, OBJECTIVE_CATEGORIES,
  OBJECTIVE_STATUS_COLORS, OBJECTIVE_STATUS_LABELS, PAUSE_REASON_LABELS, PRIORITIES,
  SESSION_STATUS_LABELS, SKILL_AREAS, TRAINING_DAYS,
} from "@/config/trainingCatalog";
import { ProgrammeStatusBadge } from "@/components/child/TrainingTab";

const CLOSED = ["completed", "cancelled", "archived"];

function WeekTimeline({ programme, reviews }) {
  const reviewed = new Set((reviews || []).map((r) => r.week_number));
  const current = programme.current_week;
  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="programme-timeline">
      {Array.from({ length: programme.planned_weeks }, (_, i) => i + 1).map((w) => (
        <div key={w} data-testid={`timeline-week-${w}`}
          className={`rounded-md border px-2 py-2 text-center text-xs font-medium ${
            reviewed.has(w) ? "bg-ukgreen-light text-ukgreen border-ukgreen/30"
            : w === current ? "bg-primary text-white border-primary"
            : current && w < current ? "bg-blue-50 text-blue-700 border-blue-200"
            : "bg-white text-slate-500 border-border"}`}>
          Week {w}
          {reviewed.has(w) && <span className="block text-[10px]">reviewed</span>}
        </div>
      ))}
    </div>
  );
}

export default function ProgrammePage() {
  const { programmeId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("programmes:manage");
  const [prog, setProg] = useState(null);
  const [denied, setDenied] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editStart, setEditStart] = useState("");
  const [editTarget, setEditTarget] = useState("");
  const [editDays, setEditDays] = useState([]);
  const [aiDrafting, setAiDrafting] = useState(false);
  // pause/extend/risk forms
  const [pauseForm, setPauseForm] = useState({ reason: "", expected_review_date: "", comments: "" });
  const [extendForm, setExtendForm] = useState({ new_target_completion_date: "", reason: "" });
  const [riskForm, setRiskForm] = useState({ reason: "", details: "" });
  const [coverForm, setCoverForm] = useState({ trainer_id: "", start_date: "", end_date: "", reason: "" });
  const [trainers, setTrainers] = useState([]);
  // final assessment
  const [recForm, setRecForm] = useState({ reason: "", evidence_summary: "", outstanding_concerns: "", recommendation: "" });
  const [reviewForm, setReviewForm] = useState({ decision: "", comments: "" });
  // objectives
  const [objForm, setObjForm] = useState(null);
  // levels
  const [levelForm, setLevelForm] = useState({ area: "", direction: "", level: "", reason: "" });
  // weekly review
  const [weekForm, setWeekForm] = useState({ week_number: "", trainer_comments: "", next_week_objectives: "" });
  const [managerComments, setManagerComments] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/programmes/${programmeId}`);
      setProg(data);
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err));
    }
  }, [programmeId]);

  useEffect(() => {
    load();
    api.get("/staff").then((r) => setTrainers((r.data.items || []).filter((s) => s.role === "travel_trainer" && s.status === "active"))).catch(() => {});
  }, [load]);

  const act = async (fn, success) => {
    setSaving(true);
    try {
      await fn();
      toast.success(success);
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (denied) {
    return (
      <div data-testid="programme-access-denied" className="flex flex-col items-center justify-center py-20 text-center">
        <ShieldAlert className="h-10 w-10 text-red-600 mb-4" />
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Access denied</h1>
        <p className="text-sm text-slate-600 max-w-md">This programme does not exist or you are not authorised. This attempt has been recorded.</p>
      </div>
    );
  }
  if (!prog) {
    return <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading programme…</div>;
  }

  const closed = CLOSED.includes(prog.status);
  const levelNames = Object.fromEntries((prog.independence_levels || []).map((l) => [l.level, l.name]));

  return (
    <div className="space-y-5" data-testid="programme-page">
      <Dialog open={dialog === "edit-start"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="edit-start-dialog">
          <DialogHeader>
            <DialogTitle>Edit programme dates &amp; training days</DialogTitle>
            <DialogDescription>Manager / Co-ordinator only. Every change is audit-logged.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start date</Label>
              <Input type="date" data-testid="edit-start-date" value={editStart} onChange={(e) => setEditStart(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Target completion date</Label>
              <Input type="date" data-testid="edit-target-date" value={editTarget} onChange={(e) => setEditTarget(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Training days</Label>
            <div className="flex flex-wrap gap-2" data-testid="edit-days">
              {TRAINING_DAYS.map((d) => (
                <button key={d.value} type="button" data-testid={`edit-day-${d.value}`}
                  onClick={() => setEditDays((cur) => (cur.includes(d.value) ? cur.filter((x) => x !== d.value) : [...cur, d.value]))}
                  className={`h-10 px-4 rounded-md border text-sm font-medium transition-colors duration-200 ${editDays.includes(d.value) ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <Button data-testid="edit-start-save" disabled={saving || (!editStart && !editTarget && editDays.length === 0)}
            onClick={() => act(() => api.patch(`/programmes/${prog.id}`, {
              ...(editStart ? { start_date: editStart } : {}),
              ...(editTarget ? { target_completion_date: editTarget } : {}),
              training_days: editDays,
            }), "Programme schedule updated")}
            className="h-11 w-full bg-primary hover:bg-brand-hover text-white font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogContent>
      </Dialog>
      <Link to={`/children/${prog.child_id}`} data-testid="programme-back-link" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to {prog.child?.child_name}
      </Link>

      {/* Header */}
      <div className="rounded-lg border border-border bg-white p-5" data-testid="programme-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900" data-testid="programme-number">{prog.programme_number}</h1>
              <PolicyLink module="programmes" />
              <ProgrammeStatusBadge status={prog.status} testid="programme-status" />
              {prog.assessment_provisional && !prog.assessment_overdue_at && (
                <Badge className="bg-amber-100 text-amber-800 border-transparent" data-testid="programme-provisional-badge">Provisional — assessment week</Badge>
              )}
              {prog.assessment_overdue_at && (
                <Badge className="bg-red-100 text-red-800 border-transparent" data-testid="programme-overdue-badge">Assessment overdue</Badge>
              )}
              {prog.behind_schedule && <Badge className="bg-red-50 text-red-700 border-transparent" data-testid="programme-behind">Behind schedule</Badge>}
            </div>
            <p className="mt-1 text-sm text-slate-600">
              {prog.child?.child_name} · {prog.child?.case_id} · Trainer: {prog.trainer_name || "—"}
            </p>
            <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <div><dt className="text-xs text-slate-500">Start</dt><dd className="font-medium">{prog.start_date || "Not started"}</dd></div>
              <div><dt className="text-xs text-slate-500">Target completion</dt><dd className="font-medium" data-testid="programme-target">{prog.target_completion_date || "—"}</dd></div>
              <div><dt className="text-xs text-slate-500">Duration</dt><dd className="font-medium">{prog.planned_weeks} weeks{prog.current_week ? ` (week ${prog.current_week})` : ""}</dd></div>
              <div><dt className="text-xs text-slate-500">Linked records</dt><dd className="font-medium text-xs">
                {prog.journey_plan_id && <Link to={`/journey-plans/${prog.journey_plan_id}`} className="text-primary hover:underline">{prog.journey_plan_ref || "Route plan"}</Link>}
                {prog.risk_assessment_ref ? ` · ${prog.risk_assessment_ref}` : ""}
              </dd></div>
            </dl>
          </div>
          <div className="flex flex-wrap gap-2">
            {canManage && !closed && (
              <Button variant="outline" className="h-11" data-testid="programme-edit-start-button"
                onClick={() => { setEditStart(prog.start_date || ""); setEditTarget(prog.target_completion_date || ""); setEditDays(prog.training_days || []); setDialog("edit-start"); }}>
                <Pencil className="h-4 w-4 mr-1" /> Edit dates &amp; days
              </Button>
            )}
            {!closed && prog.status !== "active" && prog.status !== "extended" && ["planned", "ready_to_start"].includes(prog.status) && (
              <Button data-testid="programme-start-button" onClick={() => act(() => api.post(`/programmes/${prog.id}/start`, {}), "Programme started")}
                className="h-11 bg-ukgreen hover:bg-ukgreen/90 text-white font-semibold"><Play className="h-4 w-4 mr-1" /> Start programme</Button>
            )}
            {!closed && (
              <Button data-testid="programme-new-session" variant="outline" className="h-11"
                onClick={() => act(async () => {
                  const { data } = await api.post(`/programmes/${prog.id}/sessions`, {});
                  navigate(`/sessions/${data.id}`);
                }, "Session created")}>
                <Plus className="h-4 w-4 mr-1" /> New session
              </Button>
            )}
            {canManage && !closed && (
              <>
                <Button data-testid="programme-pause-button" variant="outline" className="h-11" onClick={() => setDialog("pause")} disabled={prog.status === "paused"}>
                  <Pause className="h-4 w-4 mr-1" /> Pause
                </Button>
                {prog.status === "paused" && (
                  <Button data-testid="programme-resume-button" variant="outline" className="h-11"
                    onClick={() => act(() => api.post(`/programmes/${prog.id}/resume`), "Programme resumed")}>Resume</Button>
                )}
                <Button data-testid="programme-extend-button" variant="outline" className="h-11" onClick={() => setDialog("extend")}>
                  <CalendarPlus className="h-4 w-4 mr-1" /> Extend
                </Button>
                <Button data-testid="programme-cover-button" variant="outline" className="h-11" onClick={() => setDialog("cover")}>
                  <UserPlus className="h-4 w-4 mr-1" /> Cover trainer
                </Button>
              </>
            )}
            {!closed && (
              <Button data-testid="programme-risk-review-button" variant="outline" className="h-11 border-amber-300 text-amber-800 hover:bg-amber-50" onClick={() => setDialog("risk")}>
                <AlertTriangle className="h-4 w-4 mr-1" /> Request journey/risk review
              </Button>
            )}
            {["active", "extended"].includes(prog.status) && (
              <Button data-testid="programme-recommend-final" variant="outline" className="h-11 border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => setDialog("recommend")}>
                Recommend final assessment
              </Button>
            )}
          </div>
        </div>
        {prog.status === "awaiting_review" && prog.final_recommendation && (
          <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50 p-4" data-testid="final-recommendation">
            <p className="text-sm font-semibold text-indigo-900">Final assessment recommended by {prog.final_recommendation.trainer_name}</p>
            <p className="text-sm text-indigo-800 mt-1">{prog.final_recommendation.recommendation}</p>
            {prog.final_recommendation.outstanding_concerns && <p className="text-xs text-indigo-700 mt-1">Concerns: {prog.final_recommendation.outstanding_concerns}</p>}
            {canManage && (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <Select value={reviewForm.decision} onValueChange={(v) => setReviewForm((f) => ({ ...f, decision: v }))}>
                  <SelectTrigger data-testid="final-review-decision" className="h-11 w-64 bg-white"><SelectValue placeholder="Manager decision" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {FINAL_REVIEW_DECISIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="final-review-comments" placeholder="Comments" className="h-11 w-64 bg-white" value={reviewForm.comments}
                  onChange={(e) => setReviewForm((f) => ({ ...f, comments: e.target.value }))} />
                <Button data-testid="final-review-submit" className="h-11 bg-primary text-white" disabled={saving || !reviewForm.decision}
                  onClick={() => act(() => api.post(`/programmes/${prog.id}/final-review`, reviewForm), "Decision recorded")}>Record decision</Button>
              </div>
            )}
          </div>
        )}
        {prog.final_review && (
          <p className="mt-3 text-xs text-slate-600" data-testid="final-review-outcome">
            Manager decision: <b>{prog.final_review.decision.replace(/_/g, " ")}</b> by {prog.final_review.manager_name} · {(prog.final_review.at || "").slice(0, 10)}
          </p>
        )}
      </div>

      <WeekTimeline programme={prog} reviews={prog.weekly_reviews} />

      <details className="rounded-lg border border-border bg-white p-4 text-sm" data-testid="programme-standard-plan">
        <summary className="cursor-pointer font-semibold text-slate-800">Standard 12-week training plan (trainer guide)</summary>
        <div className="mt-3 space-y-3 text-xs text-slate-600">
          <div>
            <p className="font-semibold text-slate-800">Weeks 1–4 — Rapport, road safety &amp; the primary route</p>
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              <li>Meet the trainee at home; build rapport while keeping professional boundaries; travel the primary route to and from school.</li>
              <li>Teach road safety: pelican, zebra and pedestrian crossings, traffic islands, drop kerbs; teach the walking route between home, the bus stop and school.</li>
              <li>Personal safety: emergency contacts saved on the trainee's phone; no music while travelling; no initiating conversation with strangers; knows own address; can text/call; knows what to do if they feel unsafe, ill, bullied or lose an item.</li>
              <li>Text parents/guardian and the Co-ordinator when the trainee arrives safely each morning and afternoon (where applicable).</li>
              <li>By weeks 3–4 let the trainee lead while staying very close; prompt when necessary.</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-800">Weeks 5–7 — Separation begins, shadowing &amp; bus/tube skills</p>
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              <li>Inform parents/guardian a day before starting separation; arrange a visible meeting point (e.g. corner of their road); never leave the trainee unsupervised — shadow unseen where needed.</li>
              <li>Shadow and observe road crossings and awareness; debrief at the bus stop with praise and improvement points; repeat for at least two weeks on both journeys.</li>
              <li>Teach the bus/tube route: stops, voice indicator, landmarks and why they matter, hailing the bus, choosing the right bus, recognising where to get off and which way to travel.</li>
              <li>Bus/tube safety: sit near the driver/front near stairs on the upper deck only if downstairs is full; stay seated until the bus is stationary; Oyster/Zip card use, top-ups and underground help points.</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-800">Weeks 8–10 — Separation tasks, alternative route &amp; emergency tasks</p>
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              <li>Get off one stop before and let the trainee travel one stop alone (they must text on arrival); build up to two stops and beyond on both journeys.</li>
              <li>Teach the alternative route to school and home, explaining why a second route is needed; repeat the bus separation procedure on the alternative route.</li>
              <li>Practical emergency tasks: safe vs unsafe strangers, what to do if followed, bus diverted or broken down, getting lost (e.g. go three stops past and find the way back), losing something, feeling ill on the bus.</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-800">Weeks 11–12 — Independent travel &amp; sign-off</p>
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              <li>The trainee travels independently home → school → home; meet them at the school gates and at home in the afternoon; they call/text to confirm they are on the bus and OK.</li>
              <li>Shadow from home without being seen, observing safe travel and correct crossings.</li>
              <li>Write up the final report when the trainee is ready to be signed off.</li>
            </ul>
          </div>
        </div>
      </details>

      <Tabs defaultValue="sessions" className="w-full">
        <TabsList className="bg-white border border-border flex flex-wrap h-auto">
          <TabsTrigger value="sessions" data-testid="tab-prog-sessions">Sessions</TabsTrigger>
          <TabsTrigger value="objectives" data-testid="tab-prog-objectives">Objectives</TabsTrigger>
          <TabsTrigger value="levels" data-testid="tab-prog-levels">Independence Levels</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-prog-reviews">Weekly Reviews</TabsTrigger>
          <TabsTrigger value="details" data-testid="tab-prog-details">Details &amp; History</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Training session history ({prog.sessions?.length ?? 0})</CardTitle></CardHeader>
            <CardContent>
              {!prog.sessions?.length ? (
                <p className="text-sm text-slate-600" data-testid="sessions-empty">No sessions yet. Use “New session” to schedule or record one.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {prog.sessions.map((s) => (
                    <li key={s.id}>
                      <Link to={`/sessions/${s.id}`} data-testid={`session-row-${s.session_number}`} className="flex flex-wrap items-center gap-2 py-2.5 text-sm hover:bg-muted rounded px-1">
                        <span className="font-medium text-slate-800 w-24">{s.date || "—"}</span>
                        <span className="font-mono text-xs text-slate-500">{s.session_number}</span>
                        <span className="text-slate-600">{s.direction === "outbound" ? "Home → School" : s.direction === "return" ? "School → Home" : s.direction === "other" ? "Other" : "—"}</span>
                        <span className="text-slate-500">{s.trainer_name}</span>
                        {s.training_level && <span className="text-slate-500">L{s.training_level}</span>}
                        <Badge className={s.attendance === "completed" ? "bg-ukgreen-light text-ukgreen border-transparent" : s.attendance ? "bg-amber-100 text-amber-800 border-transparent" : "bg-slate-100 text-slate-500 border-transparent"}>
                          {ATTENDANCE_LABELS[s.attendance] || "—"}
                        </Badge>
                        {s.status === "draft" && <Badge className="bg-amber-100 text-amber-800 border-transparent" data-testid={`session-draft-${s.id}`}>DRAFT</Badge>}
                        {s.has_intervention && <Badge className="bg-red-50 text-red-700 border-transparent">Intervention</Badge>}
                        {s.amendments?.length > 0 && <Badge className="bg-blue-50 text-blue-700 border-transparent">{s.amendments.length} amendment{s.amendments.length > 1 ? "s" : ""}</Badge>}
                        <span className="ml-auto text-xs text-slate-500">{SESSION_STATUS_LABELS[s.status]}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="objectives" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Individual training objectives</CardTitle>
              <p className="text-xs text-slate-500 mt-1" data-testid="objectives-guidance">
                Objectives must be something you can <span className="font-medium">observe and evidence in a session</span> — e.g. "Owen will
                board the correct bus unaided". <span className="font-medium">Do not write vague aims like "build confidence"</span> — they
                cannot be observed, evidenced or signed off. The trainer drafts objectives at programme setup from the assessment, risk
                assessment and route plan; the Co-ordinator confirms them, and statuses are updated by hand as training progresses.
              </p>
              {!closed && (
                <Button data-testid="objective-add-button" variant="outline" className="h-11" onClick={() => setObjForm({ objective: "", category: "other", reason: "", starting_ability: "", target_outcome: "", strategy: "", priority: "standard", target_date: "" })}>
                  <Plus className="h-4 w-4 mr-1" /> Add objective
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {!prog.objectives?.length ? (
                <p className="text-sm text-slate-600" data-testid="objectives-empty">No objectives yet. Create them from the approved assessment, risk assessment and route plan.</p>
              ) : (
                <div className="space-y-3">
                  {prog.objectives.map((o) => (
                    <div key={o.id} className="rounded-md border border-border p-3" data-testid={`objective-${o.id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="bg-slate-100 text-slate-600 border-transparent">{OBJECTIVE_CATEGORIES.find((c) => c.value === o.category)?.label || o.category}</Badge>
                        {o.priority === "high" && <Badge className="bg-red-50 text-red-700 border-transparent">High priority</Badge>}
                        <span className="font-medium text-slate-900 flex-1">{o.objective}</span>
                        <Badge className={OBJECTIVE_STATUS_COLORS[o.status]} data-testid={`objective-status-${o.id}`}>{OBJECTIVE_STATUS_LABELS[o.status]}</Badge>
                      </div>
                      {o.target_outcome && <p className="mt-1 text-xs text-slate-600">Target: {o.target_outcome}</p>}
                      {!closed && (
                        <div className="mt-2 flex flex-wrap gap-2 items-center">
                          <Select value={o.status} onValueChange={(v) => act(() => api.patch(`/programmes/${prog.id}/objectives/${o.id}`, { status: v }), "Objective updated")}>
                            <SelectTrigger data-testid={`objective-status-select-${o.id}`} className="h-10 w-56"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white">
                              {Object.entries(OBJECTIVE_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <span className="text-xs text-slate-500">Statuses are set by professionals, never automatically.</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="levels" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Progressive independence levels</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="levels-table">
                  <thead><tr className="text-left text-xs text-slate-500 border-b border-border">
                    <th className="py-2 pr-3">Skill area</th><th className="py-2 pr-3">Home → School</th><th className="py-2 pr-3">School → Home</th><th className="py-2"></th>
                  </tr></thead>
                  <tbody>
                    {SKILL_AREAS.map((a) => {
                      const lv = (prog.skill_levels || {})[a.key] || {};
                      return (
                        <tr key={a.key} className="border-b border-border/50" data-testid={`level-row-${a.key}`}>
                          <td className="py-2 pr-3 font-medium text-slate-800">{a.label}</td>
                          <td className="py-2 pr-3">
                            {lv.outbound ? (
                              <div>
                                <div>{`L${lv.outbound} — ${levelNames[lv.outbound] || ""}`}</div>
                                <LevelMeaning levels={prog.independence_levels} level={lv.outbound} testid={`level-outbound-meaning-${a.key}`} />
                              </div>
                            ) : <span className="text-slate-400">Not assessed</span>}
                          </td>
                          <td className="py-2 pr-3">
                            {lv.return ? (
                              <div>
                                <div>{`L${lv.return} — ${levelNames[lv.return] || ""}`}</div>
                                <LevelMeaning levels={prog.independence_levels} level={lv.return} testid={`level-return-meaning-${a.key}`} />
                              </div>
                            ) : <span className="text-slate-400">Not assessed</span>}
                          </td>
                          <td className="py-2">
                            {!closed && (
                              <Button size="sm" variant="outline" data-testid={`level-change-${a.key}`}
                                onClick={() => { setLevelForm({ area: a.key, direction: "outbound", level: "", reason: "" }); setDialog("level"); }}>
                                Record level
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {prog.level_history?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 mb-2">Level history</h3>
                  <ul className="space-y-1 text-xs text-slate-600">
                    {prog.level_history.slice().reverse().map((h, i) => (
                      <li key={i} data-testid={`level-history-${i}`}>
                        {(h.changed_at || "").slice(0, 10)} — {SKILL_AREAS.find((a) => a.key === h.area)?.label} ({h.direction === "outbound" ? "Home→School" : "School→Home"}):
                        {" "}{h.old_level ? `L${h.old_level}` : "—"} → <b>L{h.new_level}</b> by {h.changed_by_name} · {h.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Weekly progress reviews</CardTitle>
              {!closed && (
                <Button data-testid="weekly-create-button" variant="outline" className="h-11"
                  onClick={() => { setWeekForm({ week_number: String(prog.current_week || 1), trainer_comments: "", next_week_objectives: "", young_person_feedback: "", school_family_communication: "", incidents_noted: "" }); setDialog("week"); }}>
                  <Plus className="h-4 w-4 mr-1" /> Record week review
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {(() => {
                const today = new Date().toISOString().slice(0, 10);
                const overdue = (prog.objectives || []).filter((o) => o.status === "in_progress" && o.target_date && o.target_date < today);
                if (!overdue.length) return null;
                return (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="objectives-overdue-prompt">
                    <p className="font-semibold">{overdue.length} objective{overdue.length > 1 ? "s" : ""} past their target date</p>
                    <ul className="mt-1 list-disc pl-4 space-y-0.5 text-xs">
                      {overdue.map((o) => (
                        <li key={o.id} data-testid={`objective-overdue-${o.id}`}>
                          {o.objective} <span className="text-amber-700">(target {o.target_date})</span> — review now: mark achieved, adjust or re-plan on the Objectives tab.
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
              {!prog.weekly_reviews?.length && <p className="text-sm text-slate-600" data-testid="reviews-empty">No weekly reviews recorded yet.</p>}
              {prog.weekly_reviews?.map((r) => (
                <div key={r.id} className="rounded-md border border-border p-4" data-testid={`weekly-review-${r.week_number}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-slate-900">Week {r.week_number}</p>
                    <span className="text-xs text-slate-500">{r.period_start} → {r.period_end}</span>
                    <Badge className="bg-blue-50 text-blue-700 border-transparent">{r.summary?.sessions_completed ?? 0}/{r.summary?.sessions_planned ?? 0} completed</Badge>
                    {r.summary?.interventions > 0 && <Badge className="bg-red-50 text-red-700 border-transparent">{r.summary.interventions} intervention{r.summary.interventions > 1 ? "s" : ""}</Badge>}
                    <a href={`${API_URL}/weekly-reviews/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`weekly-pdf-${r.id}`}
                      className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline"><FileText className="h-3.5 w-3.5" /> Weekly report PDF</a>
                    <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid={`weekly-send-coordinator-${r.id}`}
                      onClick={() => act(() => api.post("/report-submissions", { report_type: "weekly_review", report_id: r.id }), "Weekly report sent to the Co-ordinator")}>
                      Send to Co-ordinator
                    </Button>
                  </div>
                  {r.trainer_comments && <p className="mt-2 text-sm text-slate-700">{r.trainer_comments}</p>}
                  {r.young_person_feedback && <p className="mt-1.5 text-xs text-slate-600" data-testid={`weekly-yp-feedback-${r.id}`}><span className="font-medium text-slate-700">Young person's feedback:</span> {r.young_person_feedback}</p>}
                  {r.school_family_communication && <p className="mt-1 text-xs text-slate-600" data-testid={`weekly-school-family-${r.id}`}><span className="font-medium text-slate-700">School/family communication:</span> {r.school_family_communication}</p>}
                  {r.incidents_noted && <p className="mt-1 text-xs text-slate-600" data-testid={`weekly-incidents-noted-${r.id}`}><span className="font-medium text-slate-700">Incidents noted:</span> {r.incidents_noted}</p>}
                  {r.manager_review ? (
                    <p className="mt-2 text-xs text-ukgreen" data-testid={`weekly-manager-${r.id}`}>Reviewed by {r.manager_review.reviewer_name} · {(r.manager_review.at || "").slice(0, 10)} — {r.manager_review.comments}</p>
                  ) : canManage ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Input className="h-10 flex-1 min-w-[200px]" placeholder="Manager review comments" value={managerComments[r.id] || ""}
                        data-testid={`weekly-manager-comments-${r.id}`}
                        onChange={(e) => setManagerComments((m) => ({ ...m, [r.id]: e.target.value }))} />
                      <Button size="sm" data-testid={`weekly-manager-submit-${r.id}`} className="h-10"
                        onClick={() => act(() => api.post(`/weekly-reviews/${r.id}/manager-review`, { comments: managerComments[r.id] || "" }), "Review recorded")}>Review</Button>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-amber-700">Awaiting manager review</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Programme details &amp; history</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div><dt className="text-xs text-slate-500">Training days</dt><dd>{(prog.training_days || []).join(", ") || "—"}</dd></div>
                <div><dt className="text-xs text-slate-500">Sessions per week</dt><dd>{prog.sessions_per_week || "—"}</dd></div>
                <div><dt className="text-xs text-slate-500">Created</dt><dd>{(prog.created_at || "").slice(0, 10)} by {prog.creator_name || "—"}</dd></div>
              </dl>
              {prog.notes && <p className="text-slate-700 whitespace-pre-wrap">{prog.notes}</p>}
              {prog.cover_assignments?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">Cover trainers</h3>
                  <ul className="space-y-1 text-xs text-slate-600">
                    {prog.cover_assignments.map((c) => (
                      <li key={c.id} data-testid={`cover-${c.id}`}>
                        {c.trainer_name}: {c.start_date} → {c.end_date} {c.current ? <Badge className="bg-ukgreen-light text-ukgreen border-transparent">active</Badge> : <span className="text-slate-400">(ended/expired)</span>}
                        {c.reason ? ` — ${c.reason}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {prog.extension_history?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">Extension history (original dates preserved)</h3>
                  <ul className="space-y-1 text-xs text-slate-600">
                    {prog.extension_history.map((e, i) => (
                      <li key={i} data-testid={`extension-${i}`}>{(e.at || "").slice(0, 10)} — {e.old_target || "—"} → <b>{e.new_target}</b> ({EXTENSION_REASON_LABELS[e.reason] || e.reason}) by {e.approved_by_name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {prog.pause_history?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">Pause / resume history</h3>
                  <ul className="space-y-1 text-xs text-slate-600">
                    {prog.pause_history.map((p, i) => (
                      <li key={i} data-testid={`pause-${i}`}>{(p.at || "").slice(0, 10)} — {p.action}{p.reason ? `: ${PAUSE_REASON_LABELS[p.reason] || p.reason}` : ""}{p.expected_review_date ? ` (review ${p.expected_review_date})` : ""} by {p.by_name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <Dialog open={dialog === "pause"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Pause programme</DialogTitle><DialogDescription>All training history is preserved. Training cannot resume until a manager resumes the programme.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Reason *</Label>
              <Select value={pauseForm.reason} onValueChange={(v) => setPauseForm((f) => ({ ...f, reason: v }))}>
                <SelectTrigger data-testid="pause-reason" className="h-11"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {(prog.pause_reasons || []).map((r) => <SelectItem key={r} value={r}>{PAUSE_REASON_LABELS[r] || r.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Expected review date</Label>
              <Input data-testid="pause-review-date" type="date" value={pauseForm.expected_review_date} onChange={(e) => setPauseForm((f) => ({ ...f, expected_review_date: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Comments</Label>
              <Textarea data-testid="pause-comments" rows={3} value={pauseForm.comments} onChange={(e) => setPauseForm((f) => ({ ...f, comments: e.target.value }))} /></div>
            <Button data-testid="pause-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !pauseForm.reason}
              onClick={() => act(() => api.post(`/programmes/${prog.id}/pause`, pauseForm), "Programme paused")}>Pause programme</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "extend"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Extend programme</DialogTitle><DialogDescription>The original target date ({prog.target_completion_date || "not set"}) is preserved in history.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>New target completion date *</Label>
              <Input data-testid="extend-date" type="date" value={extendForm.new_target_completion_date} onChange={(e) => setExtendForm((f) => ({ ...f, new_target_completion_date: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Reason *</Label>
              <Select value={extendForm.reason} onValueChange={(v) => setExtendForm((f) => ({ ...f, reason: v }))}>
                <SelectTrigger data-testid="extend-reason" className="h-11"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(EXTENSION_REASON_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="extend-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !extendForm.new_target_completion_date || !extendForm.reason}
              onClick={() => act(() => api.post(`/programmes/${prog.id}/extend`, extendForm), "Programme extended")}>Extend programme</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "risk"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Request journey / risk review</DialogTitle><DialogDescription>Flags the approved Route Plan and Risk Assessment for manager review. Approved records are never altered silently.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Reason *</Label>
              <Input data-testid="risk-review-reason" placeholder="e.g. New road hazard identified" value={riskForm.reason} onChange={(e) => setRiskForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Details</Label>
              <Textarea data-testid="risk-review-details" rows={3} value={riskForm.details} onChange={(e) => setRiskForm((f) => ({ ...f, details: e.target.value }))} /></div>
            <Button data-testid="risk-review-submit" className="h-11 w-full bg-amber-600 hover:bg-amber-700 text-white" disabled={saving || !riskForm.reason.trim()}
              onClick={() => act(() => api.post(`/programmes/${prog.id}/risk-review`, riskForm), "Review requested — managers notified")}>Request review</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "cover"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Assign cover trainer</DialogTitle><DialogDescription>Cover access ends automatically after the authorised period.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Cover trainer *</Label>
              <Select value={coverForm.trainer_id} onValueChange={(v) => setCoverForm((f) => ({ ...f, trainer_id: v }))}>
                <SelectTrigger data-testid="cover-trainer-select" className="h-11"><SelectValue placeholder="Select trainer" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>From *</Label><Input data-testid="cover-start" type="date" value={coverForm.start_date} onChange={(e) => setCoverForm((f) => ({ ...f, start_date: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>To *</Label><Input data-testid="cover-end" type="date" value={coverForm.end_date} onChange={(e) => setCoverForm((f) => ({ ...f, end_date: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>Reason</Label><Input data-testid="cover-reason" value={coverForm.reason} onChange={(e) => setCoverForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            <Button data-testid="cover-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !coverForm.trainer_id || !coverForm.start_date || !coverForm.end_date}
              onClick={() => act(() => api.post(`/programmes/${prog.id}/cover-trainer`, coverForm), "Cover trainer assigned")}>Assign cover</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "recommend"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Recommend for final assessment</DialogTitle><DialogDescription>A recommendation is professional evidence for a manager to review — it never passes the child automatically.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Reason *</Label><Textarea data-testid="rec-reason" rows={2} value={recForm.reason} onChange={(e) => setRecForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Evidence / summary *</Label><Textarea data-testid="rec-evidence" rows={3} value={recForm.evidence_summary} onChange={(e) => setRecForm((f) => ({ ...f, evidence_summary: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Outstanding concerns</Label><Textarea data-testid="rec-concerns" rows={2} value={recForm.outstanding_concerns} onChange={(e) => setRecForm((f) => ({ ...f, outstanding_concerns: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Trainer recommendation *</Label><Textarea data-testid="rec-recommendation" rows={2} value={recForm.recommendation} onChange={(e) => setRecForm((f) => ({ ...f, recommendation: e.target.value }))} /></div>
            <Button data-testid="rec-submit" className="h-11 w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={saving || !recForm.reason.trim() || !recForm.evidence_summary.trim() || !recForm.recommendation.trim()}
              onClick={() => act(() => api.post(`/programmes/${prog.id}/recommend-final`, recForm), "Recommendation submitted for manager review")}>Submit recommendation</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "level"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Record independence level — {SKILL_AREAS.find((a) => a.key === levelForm.area)?.label}</DialogTitle>
            <DialogDescription>Level changes are professional judgements based on evidence — never automatic.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Direction *</Label>
              <Select value={levelForm.direction} onValueChange={(v) => setLevelForm((f) => ({ ...f, direction: v }))}>
                <SelectTrigger data-testid="level-direction" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="outbound">Home → School</SelectItem>
                  <SelectItem value="return">School → Home</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Level *</Label>
              <Select value={levelForm.level} onValueChange={(v) => setLevelForm((f) => ({ ...f, level: v }))}>
                <SelectTrigger data-testid="level-select" className="h-11"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {(prog.independence_levels || []).map((l) => (
                    <SelectItem key={l.level} value={String(l.level)} data-testid={`level-option-${l.level}`}>
                      <div className="text-left">
                        <div>L{l.level} — {l.name}</div>
                        <div className="text-xs text-slate-500 font-normal">{l.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {levelForm.level && <LevelMeaning levels={prog.independence_levels} level={levelForm.level} testid="level-select-meaning" />}
            </div>
            <div className="space-y-1.5"><Label>Professional rationale *</Label>
              <Textarea data-testid="level-reason" rows={3} value={levelForm.reason} onChange={(e) => setLevelForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            <Button data-testid="level-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !levelForm.level || !levelForm.reason.trim()}
              onClick={() => act(() => api.post(`/programmes/${prog.id}/levels`, { ...levelForm, level: parseInt(levelForm.level, 10) }), "Level recorded")}>Record level</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "week"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Weekly progress review</DialogTitle><DialogDescription>The summary is compiled from recorded sessions — factual only.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Programme week *</Label>
              <Input data-testid="week-number" type="number" min="1" max={prog.planned_weeks} value={weekForm.week_number} onChange={(e) => setWeekForm((f) => ({ ...f, week_number: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Trainer comments</Label>
              <Textarea data-testid="week-comments" rows={3} value={weekForm.trainer_comments} onChange={(e) => setWeekForm((f) => ({ ...f, trainer_comments: e.target.value }))} /></div>
            <Button type="button" variant="outline" className="h-10 w-full border-dashed border-primary/50 text-primary" data-testid="week-ai-draft" disabled={aiDrafting || !weekForm.week_number}
              onClick={async () => {
                setAiDrafting(true);
                try {
                  const { data } = await api.post("/ai/draft-weekly-review", { programme_id: prog.id, week_number: parseInt(weekForm.week_number, 10) });
                  setWeekForm((f) => ({ ...f, trainer_comments: data.draft }));
                  toast.success("AI draft inserted into trainer comments — review and edit before saving");
                } catch (err) { toast.error(formatApiError(err)); } finally { setAiDrafting(false); }
              }}>
              <Sparkles className="h-4 w-4 mr-2" /> {aiDrafting ? "Drafting…" : "Draft trainer comments with AI"}
            </Button>
            <div className="space-y-1.5"><Label>Next week's objectives</Label>
              <Textarea data-testid="week-next" rows={2} value={weekForm.next_week_objectives} onChange={(e) => setWeekForm((f) => ({ ...f, next_week_objectives: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Young person's feedback this week</Label>
              <Textarea data-testid="week-young-person-feedback" rows={2} value={weekForm.young_person_feedback || ""} onChange={(e) => setWeekForm((f) => ({ ...f, young_person_feedback: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>School / family communication</Label>
              <Textarea data-testid="week-school-family-communication" rows={2} value={weekForm.school_family_communication || ""} onChange={(e) => setWeekForm((f) => ({ ...f, school_family_communication: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Incidents noted</Label>
              <Textarea data-testid="week-incidents-noted" rows={2} value={weekForm.incidents_noted || ""} onChange={(e) => setWeekForm((f) => ({ ...f, incidents_noted: e.target.value }))} /></div>
            <Button data-testid="week-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !weekForm.week_number}
              onClick={() => act(() => api.post(`/programmes/${prog.id}/weekly-reviews`, { ...weekForm, week_number: parseInt(weekForm.week_number, 10) }), "Weekly review recorded")}>Record review</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!objForm} onOpenChange={(o) => !o && setObjForm(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add training objective</DialogTitle></DialogHeader>
          {objForm && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Objective *</Label>
                <Input data-testid="obj-text" value={objForm.objective}
                  placeholder='e.g. "Owen will identify and board the correct 432 bus home independently"'
                  onChange={(e) => setObjForm((f) => ({ ...f, objective: e.target.value }))} />
                <p className="text-xs text-slate-500">Must be observable in a session — who will do what, unaided or with what support. Vague aims like "build confidence" are not acceptable objectives.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Category</Label>
                  <Select value={objForm.category} onValueChange={(v) => setObjForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="obj-category" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{OBJECTIVE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Priority</Label>
                  <Select value={objForm.priority} onValueChange={(v) => setObjForm((f) => ({ ...f, priority: v }))}>
                    <SelectTrigger data-testid="obj-priority" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Reason (from assessment / RA / route plan)</Label><Textarea data-testid="obj-reason" rows={2} value={objForm.reason} onChange={(e) => setObjForm((f) => ({ ...f, reason: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Starting ability</Label><Textarea data-testid="obj-starting" rows={2} value={objForm.starting_ability} onChange={(e) => setObjForm((f) => ({ ...f, starting_ability: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Target outcome</Label>
                <Textarea data-testid="obj-target" rows={2} value={objForm.target_outcome}
                  placeholder='e.g. "Boards the correct bus with no prompting on 3 consecutive sessions by week 8"'
                  onChange={(e) => setObjForm((f) => ({ ...f, target_outcome: e.target.value }))} />
                <p className="text-xs text-slate-500">How you'll know it's achieved — the measurable end point and by when.</p>
              </div>
              <div className="space-y-1.5"><Label>Training strategy</Label><Textarea data-testid="obj-strategy" rows={2} value={objForm.strategy} onChange={(e) => setObjForm((f) => ({ ...f, strategy: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Target date</Label><Input data-testid="obj-date" type="date" value={objForm.target_date} onChange={(e) => setObjForm((f) => ({ ...f, target_date: e.target.value }))} /></div>
              <Button data-testid="obj-save" className="h-11 w-full bg-primary text-white" disabled={saving || !objForm.objective.trim()}
                onClick={() => act(() => api.put(`/programmes/${prog.id}/objectives`, { objectives: [...(prog.objectives || []), objForm] }), "Objective added").then(() => setObjForm(null))}>Save objective</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
